import type {
  GlMapCreateOperation,
  GlMapInOut,
} from '@festina/configuration-general-types/configuration'
import type { LayerKey } from '@festina-life/api'
import { dateUtils } from '@festina-life/core'
import { useTranslate } from '@festina-life/ui/hooks'

import { glMapCreateOperationFromInOut } from '../../uiModels/glMapOperations/helpers'
import { loadTypedDataFromFile } from '../branchCreator/helpers'
import type {
  DraftIdentifier,
  DraftSubmitStatus,
} from '../draftCreationModal/types'
import { OperationFileUploadForm } from '../operationFileUploadForm/OperationFileUploadForm'

const JSON_FILE_EXTENSION = '.json'

type Props = {
  formId: string
  layer: LayerKey
  block: string
  expectedEntityName?: string
  onStatusChanged: (status: DraftSubmitStatus) => void
}

export const GlMapJsonUploadForm = (props: Props) => {
  const translate = useTranslate()

  return (
    <OperationFileUploadForm
      {...props}
      fileConfig={{
        fileExtension: JSON_FILE_EXTENSION,
        label: translate('documentFormat.json'),
        description: translate('glMapJsonUploadForm.file.description'),
        invalidFileTypeError: translate('glMapJsonUploadForm.file.invalidType'),
        convertToOperation: async (jsonFile) =>
          glMapCreateOperationFromInOut(
            await loadTypedDataFromFile<GlMapInOut>(jsonFile, 'GlMapInOut'),
          ),
      }}
      entityConfig={{
        getEntityId: (operation: GlMapCreateOperation) =>
          ({
            type: 'glMap',
            name: operation.glMap?.mappingId ?? '',
            fromDate: operation.glMap?.from
              ? dateUtils.dateUtcAsLocal(operation.glMap.from).toDate()
              : undefined,
            layer: props.layer,
            block: props.block,
          }) satisfies DraftIdentifier,
        missingEntityNameError: translate('glMapDraftForm.missingMappingName'),
        getEntityNameMismatchError: (mappingName, expectedMappingName) =>
          translate('glMapDraftForm.mappingNameMismatch', {
            mappingName,
            expectedMappingName,
          }),
      }}
      getUploadErrorMessage={(error) =>
        translate('glMapDraftForm.error', { error })
      }
    />
  )
}
